import { lazy, Suspense, useEffect, useState } from "react";
import { Plus, Target, Unlock } from "lucide-react";
import type { DashboardData, Owner, SavingGoal, Transaction } from "../../types";
import { money, todayIn } from "../../lib/finance";
import { floatingPoolSummaries, goalCurrentSaved } from "../../lib/goals";
import SecondaryHeading from "../../app/SecondaryHeading";
import Empty from "../../components/Empty";
import Avatar from "../../components/Avatar";
import GoalCard from "./GoalCard";
import GoalForm from "./GoalForm";
import GoalTopUpModal from "./GoalTopUpModal";
import GoalUnfloatModal from "./GoalUnfloatModal";
const GoalDetail = lazy(() => import("./GoalDetail"));

export interface GoalsProps {
  data: DashboardData;
  profile: Owner;
  onSaved: () => Promise<unknown> | void;
  onOpenTransaction?: (t: Transaction) => void;
  focusGoalId?: string | null;
  onFocusHandled?: () => void;
}

export default function Goals({
  data,
  profile: _profile,
  onSaved,
  onOpenTransaction,
  focusGoalId,
  onFocusHandled,
}: GoalsProps) {
  const [formGoal, setFormGoal] = useState<SavingGoal | null | "new">(null);
  const [topUpGoal, setTopUpGoal] = useState<{ goal: SavingGoal; mode: "topup" | "skip" | "resume" } | null>(null);
  const [unfloatGroup, setUnfloatGroup] = useState<string | null>(null);
  const [detailGoal, setDetailGoal] = useState<SavingGoal | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<string>("all");

  useEffect(() => {
    if (focusGoalId) {
      const target = (data.goals || []).find((g) => g.id === focusGoalId);
      if (target) {
        setSelectedProfile("all");
        setDetailGoal(target);
      }
      onFocusHandled?.();
    }
  }, [focusGoalId, data.goals, onFocusHandled]);

  const today = todayIn(data.timezone);
  const currentMonth = today.slice(0, 7);
  const goals = data.goals || [];

  const filteredGoals = goals.filter((g) => {
    if (selectedProfile === "all") return true;
    if (selectedProfile === "household") return !g.profile;
    return g.profile === selectedProfile;
  });

  const activeGoals = filteredGoals.filter((g) => g.status === "active");
  const achievedGoals = filteredGoals.filter((g) => g.status === "achieved");
  const floatingPools = floatingPoolSummaries(filteredGoals);
  const independentActiveGoals = activeGoals.filter((g) => !g.floatingGroup);

  const totalSaved = activeGoals.reduce((sum, g) => sum + goalCurrentSaved(g, currentMonth), 0);
  const profilesList = (data.profiles || []).map((p) => p.name);

  function handleSaveSuccess() {
    setFormGoal(null);
    setTopUpGoal(null);
    setUnfloatGroup(null);
    setDetailGoal(null);
    void onSaved();
  }

  return (
    <section className="goals-panel panel settings-panel">
      <SecondaryHeading
        title="Savings goals"
        icon={<Target size={18} />}
      >
        <button
          type="button"
          className="primary small"
          onClick={() => setFormGoal("new")}
        >
          <Plus size={14} /> Add goal
        </button>
      </SecondaryHeading>

      {goals.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", padding: "2rem 0" }}>
          <Empty
            title="No savings goals yet"
            description="Create your first goal to start saving for a vacation, gadget or emergency fund."
          />
          <button
            type="button"
            className="primary small"
            onClick={() => setFormGoal("new")}
          >
            <Plus size={14} /> Add goal
          </button>
        </div>
      ) : (
        <>
          {/* Top summary stats without nested boxes */}
          <div className="goals-summary-bar">
            <div className="goals-summary-stat">
              <span className="goals-summary-label">Total saved</span>
              <strong className="goals-summary-value">{money(totalSaved, data.baseCurrency)}</strong>
            </div>
            <div className="goals-summary-stat">
              <span className="goals-summary-label">Active targets</span>
              <strong className="goals-summary-value">{activeGoals.length}</strong>
            </div>
          </div>

          {/* Profile filter tabs */}
          <div className="goals-filter-tabs" role="tablist" aria-label="Filter goals by owner">
            <button
              type="button"
              role="tab"
              aria-selected={selectedProfile === "all"}
              className={selectedProfile === "all" ? "selected" : ""}
              onClick={() => setSelectedProfile("all")}
            >
              <span className="goals-filter-icon" aria-hidden="true">
                <Target size={14} strokeWidth={2.2} />
              </span>
              <span>All</span>
              <span className="goals-filter-count">{goals.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={selectedProfile === "household"}
              className={selectedProfile === "household" ? "selected" : ""}
              onClick={() => setSelectedProfile("household")}
            >
              <Avatar owner="Household" size={20} />
              <span>Household</span>
              <span className="goals-filter-count">{goals.filter((g) => !g.profile).length}</span>
            </button>
            {profilesList.map((p) => {
              const count = goals.filter((g) => g.profile === p).length;
              return (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={selectedProfile === p}
                  className={selectedProfile === p ? "selected" : ""}
                  onClick={() => setSelectedProfile(p)}
                >
                  <Avatar owner={p as Owner} size={20} />
                  <span>{p}</span>
                  <span className="goals-filter-count">{count}</span>
                </button>
              );
            })}
          </div>

          {filteredGoals.length === 0 && (
            <Empty
              title="No goals found"
              description={
                selectedProfile === "household"
                  ? "No savings goals specifically assigned to the household."
                  : `No savings goals assigned to ${selectedProfile}.`
              }
            />
          )}

          {/* Shared / Floating Pools without outer card box */}
          {floatingPools.map((pool) => (
            <div key={pool.groupName} className="goals-group">
              <div className="section-heading">
                <div className="goals-group-title">
                  <h3>{pool.groupName} pool</h3>
                  <span className="subtle-tag">{pool.goals.length}</span>
                  <span className="goals-pool-desc">
                    Combined {money(pool.totalSaved)} saved across {pool.goals.length} items
                  </span>
                </div>
                <button
                  type="button"
                  className="small"
                  onClick={() => setUnfloatGroup(pool.groupName)}
                >
                  <Unlock size={13} /> Split pool
                </button>
              </div>
              <div className="goals-grid">
                {pool.goals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    today={today}
                    month={currentMonth}
                    accounts={data.accounts}
                    onOpen={(g) => setDetailGoal(g)}
                    onTopUp={(g) => setTopUpGoal({ goal: g, mode: "topup" })}
                    onSkipMonth={(g) => setTopUpGoal({ goal: g, mode: "skip" })}
                    onResumeMonth={(g) => setTopUpGoal({ goal: g, mode: "resume" })}
                    onUnfloat={() => setUnfloatGroup(pool.groupName)}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Independent Active Goals Grid */}
          {independentActiveGoals.length > 0 && (
            <div className="goals-group">
              <div className="section-heading">
                <div className="goals-group-title">
                  <h3>Active goals</h3>
                  <span className="subtle-tag">{independentActiveGoals.length}</span>
                </div>
              </div>
              <div className="goals-grid">
                {independentActiveGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    today={today}
                    month={currentMonth}
                    accounts={data.accounts}
                    onOpen={(g) => setDetailGoal(g)}
                    onTopUp={(g) => setTopUpGoal({ goal: g, mode: "topup" })}
                    onSkipMonth={(g) => setTopUpGoal({ goal: g, mode: "skip" })}
                    onResumeMonth={(g) => setTopUpGoal({ goal: g, mode: "resume" })}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Achieved Goals Grid */}
          {achievedGoals.length > 0 && (
            <div className="goals-group">
              <div className="section-heading">
                <div className="goals-group-title">
                  <h3>Achieved goals</h3>
                  <span className="subtle-tag">{achievedGoals.length}</span>
                </div>
              </div>
              <div className="goals-grid">
                {achievedGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    today={today}
                    month={currentMonth}
                    accounts={data.accounts}
                    onOpen={(g) => setDetailGoal(g)}
                    onTopUp={(g) => setTopUpGoal({ goal: g, mode: "topup" })}
                    onSkipMonth={(g) => setTopUpGoal({ goal: g, mode: "skip" })}
                    onResumeMonth={(g) => setTopUpGoal({ goal: g, mode: "resume" })}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {formGoal && (
        <GoalForm
          goal={formGoal === "new" ? null : formGoal}
          accounts={data.accounts}
          profiles={profilesList}
          baseCurrency={data.baseCurrency}
          today={today}
          defaultProfile={selectedProfile !== "all" && selectedProfile !== "household" ? selectedProfile : undefined}
          existingPools={[...new Set(goals.map((g) => g.floatingGroup).filter((p): p is string => Boolean(p)))]}
          onClose={() => setFormGoal(null)}
          onSaved={handleSaveSuccess}
        />
      )}

      {topUpGoal && (
        <GoalTopUpModal
          goal={topUpGoal.goal}
          goals={goals}
          transactions={data.transactions}
          accounts={data.accounts}
          profiles={profilesList}
          today={today}
          month={currentMonth}
          initialMode={topUpGoal.mode}
          onClose={() => setTopUpGoal(null)}
          onSaved={handleSaveSuccess}
        />
      )}

      {unfloatGroup && (
        <GoalUnfloatModal
          goals={goals.filter((g) => g.floatingGroup === unfloatGroup)}
          accounts={data.accounts}
          groupName={unfloatGroup}
          onClose={() => setUnfloatGroup(null)}
          onSaved={handleSaveSuccess}
        />
      )}

      {detailGoal && (
        <Suspense fallback={<p role="status">Opening goal…</p>}>
        <GoalDetail
          goal={detailGoal}
          goals={goals}
          today={today}
          accounts={data.accounts}
          transactions={data.transactions}
          onClose={() => setDetailGoal(null)}
          onEdit={() => { const g = detailGoal; setDetailGoal(null); setFormGoal(g); }}
          onTopUp={() => { const g = detailGoal; setDetailGoal(null); setTopUpGoal({ goal: g, mode: "topup" }); }}
          onOpenTransaction={onOpenTransaction}
          onDeleted={handleSaveSuccess}
        />
        </Suspense>
      )}
    </section>
  );
}
